/**
 * Shared Color Constants and Utilities
 * 
 * Centralized mapping for branding colors to Tailwind classes
 * and helper functions for theme-aware components.
 */

// Map schema color tokens to section utility classes defined in globals.css
// These set background + text color + section divider color together
export const sectionColorMapping: Record<string, string> = {
    'white': 'section-aq-white',
    'lightest-gray': 'section-aq-lightest-gray',
    'navy': 'section-aq-navy',
    'orange': 'section-aq-orange',
    'electric-blue': 'section-aq-electric-blue',
    'sky-blue': 'section-aq-sky-blue',
    'goldenrod': 'section-aq-goldenrod',
    'off-white': 'section-aq-off-white',
    'black': 'section-aq-black',
    'transparent': 'section-aq-transparent',
};

// Backwards compatibility alias - prefer sectionColorMapping for new code
export const bgColorMapping = sectionColorMapping;

/**
 * Determine if a background color token is considered "dark"
 * used for automated text contrast and overlay logic.
 */
export function isDarkColor(colorToken?: string): boolean {
    if (!colorToken) return false;
    const darkTokens = ['navy', 'black', 'electric-blue', 'orange'];
    return darkTokens.includes(colorToken);
}

/**
 * Get section class from color token (sets bg + text color + divider)
 */
export function getSectionColorClass(color: string | undefined | null): string {
    if (!color) return 'section-aq-white';
    return sectionColorMapping[color] || 'section-aq-white';
}

/**
 * Backwards compatibility alias - prefer getSectionColorClass for new code
 */
export function getBgColorClass(color: string | undefined | null): string {
    return getSectionColorClass(color);
}

/**
 * Get the 6-color gradient palette for a given scheme
 */
import colorSchemes from '@/theme/tokens/color-schemes.json';
import colorTokens from '@/theme/tokens/colors.json';

function resolveColor(tokenName: string): string {
  // Handle direct hex values
  if (tokenName.startsWith('#')) return tokenName;

  // Search in brand, neutral, etc.
  for (const group of Object.values(colorTokens)) {
    if (typeof group === 'object' && tokenName in group) {
      return (group as Record<string, string>)[tokenName];
    }
  }

  return '#ffffff'; // Fallback
}

/**
 * Adjust the lightness of a hex color by a percentage
 * @param hex - The hex color (e.g., '#041246')
 * @param percent - Positive to lighten, negative to darken (e.g., 10 or -10)
 */
function adjustLightness(hex: string, percent: number): string {
  // Parse hex to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Convert to HSL
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
      case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
      case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
    }
  }

  // Adjust lightness
  const newL = Math.max(0, Math.min(1, l + percent / 100));

  // Convert back to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let rNew, gNew, bNew;
  if (s === 0) {
    rNew = gNew = bNew = newL;
  } else {
    const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    const p = 2 * newL - q;
    rNew = hue2rgb(p, q, h + 1/3);
    gNew = hue2rgb(p, q, h);
    bNew = hue2rgb(p, q, h - 1/3);
  }

  // Convert back to hex
  const toHex = (c: number) => Math.round(c * 255).toString(16).padStart(2, '0');
  return `#${toHex(rNew)}${toHex(gNew)}${toHex(bNew)}`;
}

// In-memory cache for scheme gradient colors
// Prevents recalculating the 9-color palette for the same input scheme
const schemeGradientCache = new Map<string, string[]>();

export function getSchemeGradientColors(schemeName: string = 'white'): string[] {
  // Return cached result if available
  if (schemeGradientCache.has(schemeName)) {
    return schemeGradientCache.get(schemeName)!;
  }

  // 1. Get the scheme definition
  const schemes = colorSchemes.schemes as unknown as Record<string, Record<string, string | number>>;
  const scheme = schemes[schemeName] || schemes['white'];

  // 2. Get background, primary, and accent colors
  const bgColor = resolveColor(scheme.background as string);
  const primaryColor = resolveColor(scheme.primary as string);
  const accentColor = resolveColor(scheme.accent as string);

  // 3. Generate the 9-color palette:
  //    - background
  //    - primary (lower-left emphasis)
  //    - accent (upper-right emphasis)
  //    - 6 background variations for scattered depth (+/-20%, +/-10%, +/-5%)
  const result = [
    bgColor,
    primaryColor,
    accentColor,
    adjustLightness(bgColor, -20),
    adjustLightness(bgColor, 20),
    adjustLightness(bgColor, -10),
    adjustLightness(bgColor, 10),
    adjustLightness(bgColor, -5),
    adjustLightness(bgColor, 5),
  ];

  // Cache the result
  // Limit cache size to 50 entries to prevent memory leaks (though schemes are few)
  if (schemeGradientCache.size >= 50) {
    const firstKey = schemeGradientCache.keys().next().value;
    if (firstKey !== undefined) {
      schemeGradientCache.delete(firstKey);
    }
  }
  schemeGradientCache.set(schemeName, result);

  return result;
}
